import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { MdVisibility, MdVisibilityOff } from 'react-icons/md'
import ValidationSummary from '@/components/common/ValidationSummary'
import LoginHelpTrigger from '@/components/auth/login/LoginHelpTrigger'
import type { Identifier } from '@/hooks/useLoginFlow'
import type { Channel } from '@/services/authService'

/**
 * Step 2 — registered portal user. Collects their password.
 *
 * Copy is generic by design: no first-name, no masked email, nothing
 * the customer didn't type themselves. The /auth/check-identity
 * endpoint is unauthenticated and would otherwise turn into an
 * email-enumeration oracle if it echoed display names back. Customer
 * sees their full identity only AFTER authenticating and landing on
 * the dashboard.
 *
 * Two action paths get equal weight:
 *   - "Sign in" with a password (primary)
 *   - a one-time sign-in link (secondary, full-width button)
 *
 * The link buttons follow what we can actually reach the customer on, which
 * check-identity has already told us. Roughly a quarter of customers have only
 * a mobile, and the button used to promise them an email while the backend
 * sent a text. A smaller group has neither, and used to get a button that
 * could only fail; they see no link option at all now and are pointed at help
 * instead.
 *
 * Deliberately no masked address or number on the buttons, for the same reason
 * the rest of the copy is generic: this screen is reachable unauthenticated.
 * The customer picks a channel, not a destination.
 *
 * "Remember me" is a frontend-only switch: when on, the auth token is
 * stored in localStorage (current default, persistent); when off it
 * goes to sessionStorage (gone on tab close). Backend doesn't need to
 * know.
 */

interface Props {
  identifier: Identifier
  loading: boolean
  apiError: string
  fieldErrors: Record<string, string[]>
  onSignIn: (password: string, rememberMe: boolean) => void
  onRequestMagicLink: (channel?: 'email' | 'sms') => void
  onBack: () => void
  /** Where we can reach this customer, from check-identity. */
  channels: Channel[]
}

function LoginPasswordStep({
  identifier,
  loading,
  apiError,
  fieldErrors,
  onSignIn,
  onRequestMagicLink,
  onBack,
  channels,
}: Props): React.JSX.Element {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [localError, setLocalError] = useState('')
  const [rememberMe, setRememberMe] = useState(true)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (!password) {
      setLocalError('Password is required')
      return
    }
    setLocalError('')
    onSignIn(password, rememberMe)
  }

  return (
    <div className="login-step">
      <h1 className="login-step-heading">You&rsquo;re nearly there!</h1>
      <p className="login-step-subheading">
        Enter your password to sign in.
      </p>

      <ValidationSummary errors={fieldErrors} message={apiError} />

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Password
          </label>
          <div className="password-wrapper">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className={`form-control form-control-lg ${localError ? 'is-invalid' : ''}`}
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((p) => !p)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </button>
          </div>
          {localError && <span className="form-text is-invalid">{localError}</span>}
        </div>

        <div className="login-remember-row">
          <label className="login-remember">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loading}
            />
            <span>Remember me on this device</span>
          </label>
          <Link
            to={
              identifier.mode === 'email' && identifier.email
                ? `/forgot-password?email=${encodeURIComponent(identifier.email)}`
                : '/forgot-password'
            }
            className="forgot-password-link"
          >
            Forgot your password?
          </Link>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={loading}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>

        {channels.length > 0 && (
          <>
            <div className="login-or-divider">or</div>

            {/* Sits side by side when there are two, full width when there
                is one. No conditional needed: a single flex child fills the
                row on its own. */}
            <div className="login-link-options">
              {channels.map((channel) => (
                <button
                  key={channel.type}
                  type="button"
                  className="btn btn-secondary btn-lg"
                  onClick={() => onRequestMagicLink(channel.type)}
                  disabled={loading}
                >
                  {channel.type === 'email'
                    ? 'Email me a one-time sign-in link'
                    : 'Text me a one-time sign-in link'}
                </button>
              ))}
            </div>
          </>
        )}
      </form>

      <div className="login-step-footer">
        <button type="button" className="btn btn-link" onClick={onBack}>
          Back
        </button>
        <LoginHelpTrigger identifier={identifier} />
      </div>
    </div>
  )
}

export default LoginPasswordStep
